import React from 'react'
import { Button } from '@/components/Buttons/Button'
import { TextInput } from '@/components/Inputs'
import { useForm } from 'react-hook-form'
import { AuthErrorType, AuthOptionType } from '@/actors/AuthActor/types'
import { ILoginRequest } from '@/types/auth'
import { useTranslation } from '@/localization/useTranslation'
import { STANDARD_AUTH_FORMS, EMAIL_REGEX } from '@/constants/auth'
import { useAuth } from '@/hooks/auth/useAuth'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { AuthErrorModal } from '@/actors/AuthActor/components/AuthErrorModal'
import { Stack } from '@/components/Stack'
import { FormHeader } from '@/actors/AuthActor/components/FormHeader'


interface LoginProps {
  handleNavigateToForm: (path: string) => void
  form?: AuthOptionType['options']['login']
}

export const Login: React.FC<LoginProps> = (props) => {
  const { handleNavigateToForm, form } = props
  const { loginMutation } = useAuth()
  const { isPending, error, reset } = loginMutation
  const { t } = useTranslation()

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting }
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    }
  })

  const handleError = () => {
    reset()
  }

  const handleRedirect = (path: string) => {
    reset()
    handleNavigateToForm(path)
  }

  const handleForgotPassword = () => {
    handleNavigateToForm(STANDARD_AUTH_FORMS.RESET_PASSWORD)
  }

  const onSubmit = async (data: { username: string; password: string }) => {
    // Guards against a second submit while the first is in flight: that would
    // mint a second reCAPTCHA token seconds after the first, which is exactly
    // when v3 depresses the score — and a low score is not retryable, so the
    // user's own second press is what rejects them.
    //
    // RHF's own `isSubmitting` rather than the mutation's `isPending`: it is set
    // before validation is awaited, so it covers the whole submit, and it resets
    // itself when the promise settles.
    if (isSubmitting) return

    try {
      await loginMutation.mutateAsync(data as ILoginRequest)
    } catch (err) {
      console.error('Error during login:', err)
    }
  }

  return (
    <>
      <LoadingOverlay isLoading={isPending} />
      <AuthErrorModal
        error={error as unknown as AuthErrorType}
        handleError={handleError}
        handleRedirect={handleRedirect}
      />
      <FormHeader
        title={form?.title || 'Log in'}
        subtitle={form?.subtitle}
        message={<>*{t('Required fields')}</>}
        titleTestId="auth-form-title-login"
        messageTestId="auth-form-message-login"
      />
      <form action="#" onSubmit={handleSubmit(onSubmit)}>
        <Stack mb="md">
          <TextInput
            errors={errors}
            type="email"
            label={t('Email')}
            data-testid="auth-form-login-input-email"
            required
            {...register('username', {
              required: {
                value: true,
                message: t('Email is required')
              },
              pattern: {
                value: EMAIL_REGEX,
                message: t('Invalid email')
              },
            })}
          />
          <TextInput
            errors={errors}
            type="password"
            label={t('Password')}
            data-testid="auth-form-login-input-password"
            required
            {...register('password', {
              required: {
                value: true,
                message: t('Password is required')
              },
            })}
          />
        </Stack>


        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || isSubmitting}
          fullWidth
          data-testid="auth-form-button-login-submit"
        >
          {t('Continue')}
        </Button>
        <Button
          onClick={() => handleNavigateToForm(STANDARD_AUTH_FORMS.MAIN)}
          variant="secondary"
          fullWidth
          data-testid="auth-form-button-login-back"
        >
          {t('Back')}
        </Button>
        <Button
          variant="quaternary"
          onClick={handleForgotPassword}
          fullWidth
          data-testid="auth-form-button-login-forgotPassword"
        >
          {t('I forgot my password')}
        </Button>
      </form>
    </>
  )
}